import { createRouter, createWebHistory } from "vue-router";
import Swal from "sweetalert2";
import DashboardLayout from "@/layouts/DashboardLayout.vue";
import { hasRouteAccess, getDefaultRoute } from "@/config/rbac";

const showAccessDeniedToast = () => {
  Swal.fire({
    toast: true,
    position: "top-end",
    icon: "error",
    title: "You do not have permission to access this page",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Auth Routes (Public)
    {
      path: "/login",
      name: "login",
      component: () => import("@/views/auth/LoginView.vue"),
      meta: { requiresGuest: true, title: "Login" },
    },
    {
      path: "/register",
      name: "register",
      component: () => import("@/views/auth/RegisterView.vue"),
      meta: { requiresGuest: true, title: "Register" },
    },

    // Error Routes (Public)
    {
      path: "/error/:code?",
      name: "error",
      component: () => import("@/views/ErrorPage.vue"),
      meta: { title: "Error" },
    },

    // ASN Summary Print (Without Layout)
    {
      path: "/inbound/asn/:id/summary",
      name: "asn-summary-print",
      component: () => import("@/views/inbound/AsnSummaryPrint.vue"),
      meta: { requiresAuth: true, title: "ASN Summary" },
    },

    // Update Location Print (Without Layout)
    {
      path: "/inbound/update-location",
      name: "update-location-print",
      component: () => import("@/views/inbound/UpdateLocationPrint.vue"),
      meta: { requiresAuth: true, title: "Update Location" },
    },

    // Pallet Item Detail (Without Layout)
    {
      path: "/inbound/pallet-item",
      name: "pallet-item-detail",
      component: () => import("@/views/inbound/PalletItemDetail.vue"),
      meta: { requiresAuth: true, title: "Pallet Item Detail" },
    },

    // Dashboard Routes (Protected)
    {
      path: "/",
      component: DashboardLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: "",
          name: "dashboard",
          component: () => import("@/views/dashboard/DashboardView.vue"),
          meta: { title: "Dashboard" },
        },
        {
          path: "users",
          name: "users",
          component: () => import("@/views/dashboard/UsersView.vue"),
          meta: { title: "Users" },
        },
        {
          path: "inbound",
          children: [
            {
              path: "asn",
              name: "asn",
              component: () => import("@/views/inbound/AsnView.vue"),
              meta: { title: "ASN" },
            },
            {
              path: "asn-create/:id?",
              name: "asn-create",
              component: () => import("@/views/inbound/AsnCreateManagement.vue"),
              meta: { title: "ASN Create Management" },
            },
            // {
            //   path: "asn/:id",
            //   name: "asn-detail",
            //   component: () => import("@/views/inbound/AsnDetail.vue"),
            //   meta: { title: "ASN Details" },
            // },
            {
              path: "asn/:id/management",
              name: "asn-management",
              component: () => import("@/views/inbound/AsnViewManagement.vue"),
              meta: { title: "ASN Management" },
            },
            {
              path: "asn/:id/photos",
              name: "asn-photos",
              component: () => import("@/views/inbound/AsnPhotos.vue"),
              meta: { title: "ASN Photos" },
            },
            {
              path: "asn/:id/bapb",
              name: "asn-bapb",
              component: () => import("@/views/inbound/BAPB.vue"),
              meta: { title: "BAPB" },
            },
            {
              path: "tally-in",
              name: "tally-in",
              component: () => import("@/views/inbound/TallyInView.vue"),
              meta: { title: "Tally In", allowedRoles: ["checker"] },
            },
            {
              path: "tally-in/:id",
              name: "tally-items",
              component: () => import("@/views/inbound/TallyItemsView.vue"),
              meta: { title: "Tally Items", allowedRoles: ["checker"] },
            },
            {
              path: "audit-putaway",
              name: "audit-putaway",
              component: () => import("@/views/inbound/AuditPutawayView.vue"),
              meta: { title: "Audit Putaway" },
            },
            {
              path: "good-receipt",
              name: "good-receipt",
              component: () => import("@/views/inbound/GoodReceiptView.vue"),
              meta: { title: "Good Receipt" },
            },
            {
              path: "categories",
              name: "categories",
              component: () => import("@/views/dashboard/inventory/CategoriesView.vue"),
              meta: { title: "Categories" },
            },
            {
              path: "stock",
              name: "stock",
              component: () => import("@/views/dashboard/inventory/StockView.vue"),
              meta: { title: "Stock Levels" },
            },
          ],
        },
        {
          path: "outbound",
          children: [
            {
              path: "delivery-orders",
              name: "delivery-orders",
              component: () => import("@/views/outbound/DeliveryOrders.vue"),
              meta: { title: "Delivery Orders" },
            },
            // Old Codes
            {
              path: "create-delivery-orders",
              name: "create-delivery-orders",
              component: () => import("@/views/outbound/CreateDeliveryOrders.vue"),
              meta: { title: "Create Delivery Orders" },
            },
            {
              path: "edit-delivery-orders",
              name: "edit-delivery-orders",
              component: () => import("@/views/outbound/EditDeliveryOrders.vue"),
              meta: { title: "Edit Delivery Orders" },
            },
            // New Codes
            {
              path: "delivery-orders-list",
              name: "delivery-orders-list",
              component: () => import("@/views/outbound-new/DeliveryOrdersList.vue"),
              meta: { title: "Delivery Orders List" },
            },
            {
              path: "delivery-order-create/:id?",
              name: "delivery-order-create",
              component: () => import("@/views/outbound-new/DeliveryOrderCreateManagement.vue"),
              meta: { title: "Create/Edit Delivery Order" },
            },
            {
              path: "delivery-order/:id/management",
              name: "delivery-order-management",
              component: () => import("@/views/outbound-new/DeliveryOrderManagement.vue"),
              meta: { title: "Delivery Order Management" },
            },
            {
              path: "delivery-order/:id/bapb",
              name: "do-bapb",
              component: () => import("@/views/outbound-new/DoBapb.vue"),
              meta: { title: "DO BAPB" },
            },
            {
              path: "delivery-order/:id/photos",
              name: "do-photos",
              component: () => import("@/views/outbound-new/DeliveryOrderPhotos.vue"),
              meta: { title: "DO Photos" },
            },
          ],
        },
        {
          path: "master",
          children: [
            {
              path: "customer",
              name: "customer",
              component: () => import("@/views/master/customer/CustomerView.vue"),
              meta: { title: "Customers" },
            },
            {
              path: "company",
              name: "company",
              component: () => import("@/views/master/company/CompanyView.vue"),
              meta: { title: "Company Management" },
            },
            {
              path: "uom",
              name: "uom",
              component: () => import("@/views/master/uom/UomView.vue"),
            },
            {
              path: "product-group",
              name: "product-group",
              component: () => import("@/views/master/product-group/ProductGroupView.vue"),
            },
            {
              path: "item",
              name: "item",
              component: () => import("@/views/master/item/ItemView.vue"),
            },
            {
              path: "full-pallet",
              name: "full-pallet",
              component: () => import("@/views/master/full-pallet/FullPalletView.vue"),
            },
            {
              path: "origin-address",
              name: "origin-address",
              component: () => import("@/views/master/origin-address/OriginAddressView.vue"),
            },
            {
              path: "outbound-address",
              name: "outbound-address",
              component: () => import("@/views/master/outbound-address/OutboundAddressView.vue"),
            },
            {
              path: "transporter",
              name: "transporter",
              component: () => import("@/views/master/transporter/TransporterView.vue"),
            },
            {
              path: "address",
              name: "address",
              component: () => import("@/views/master/address/AddressView.vue"),
              meta: { title: "Address Management" },
            },
            {
              path: "user",
              name: "user",
              component: () => import("@/views/master/user/UserView.vue"),
              meta: { title: "User Management" },
            },
            {
              path: "user/roles",
              name: "userRole",
              component: () => import("@/views/master/user/UserRoleView.vue"),
              meta: { title: "User Role Management" },
            },
            {
              path: "warehouse",
              name: "warehouse",
              component: () => import("@/views/master/warehouse/WarehouseView.vue"),
              meta: { title: "Warehouse Management" },
            },
            {
              path: "gate",
              name: "gate",
              component: () => import("@/views/master/gate/GateView.vue"),
              meta: { title: "Gate Management" },
            },
            {
              path: "/master/location-management",
              name: "location-management",
              component: () => import("@/views/master/location/LocationView.vue"),
              meta: { title: "New Location Management" },
            },
            {
              path: '/master/location-management/:id',
              name: "location-management-detail",
              component: () => import("@/views/master/location/RoomDetailView.vue"),
              meta: { title: "Room Detail View" },
            },
            {
              path: "/master/full-pallet-config",
              name: "full-pallet-config",
              component: () => import("@/views/master/full-pallet-config/FullPalletConfigView.vue"),
              meta: { title: "Full Pallet Configuration" },
            },
            {
              path: "/master/rack-setting",
              name: "rack-setting",
              component: () => import("@/views/master/rack-setting/RackSettingView.vue"),
              meta: { title: "Rack Setting" },
            },
            {
              path: "/master/aging-config",
              name: "aging-config",
              component: () => import("@/views/master/aging-config/AgingConfig.vue"),
              meta: { title: "Aging Configuration" },
            },
          ],
        },
        {
          path: "orders",
          children: [
            {
              path: "",
              name: "orders",
              component: () => import("@/views/dashboard/orders/OrdersView.vue"),
              meta: { title: "All Orders" },
            },
            {
              path: "pending",
              name: "orders-pending",
              component: () => import("@/views/dashboard/orders/PendingView.vue"),
              meta: { title: "Pending Orders" },
            },
            {
              path: "shipped",
              name: "orders-shipped",
              component: () => import("@/views/dashboard/orders/ShippedView.vue"),
              meta: { title: "Shipped Orders" },
            },
          ],
        },
        {
          path: "inventory",
          children: [
            {
              path: "stock-on-hand",
              name: "stock-on-hand",
              component: () => import("@/views/inventory/StockOnHandView.vue"),
              meta: { title: "Stock On Hand" },
            },
            {
              path: "management",
              name: "inventory-management",
              component: () => import("@/views/inventory/ManagementView.vue"),
              meta: { title: "Inventory Management" },
            },
            {
              path: "ticket",
              name: "inventory-ticket",
              component: () => import("@/views/inventory/TicketView.vue"),
              meta: { title: "Inventory Ticket" },
            },
            {
              path: "block-management",
              name: "block-management",
              component: () => import("@/views/inventory/BlockManagement.vue"),
              meta: { title: "Block Management" },
            },
          ],
        },
        {
          path: "inventory-management",
          children: [
            {
              path: "transfer-pallet",
              name: "transfer-pallet",
              component: () => import("@/views/inventory-management/transfer-pallet/TransferPalletView.vue"),
              meta: { title: "Transfer Pallet" },
            },
            {
              path: "transfer-pallet/create",
              name: "transfer-pallet-create",
              component: () => import("@/views/inventory-management/transfer-pallet/CreateTransferPallet.vue"),
              meta: { title: "Create Transfer Pallet" },
            },
            {
              path: "swap-pallet",
              name: "swap-pallet",
              component: () => import("@/views/inventory-management/swap-pallet/SwapPalletView.vue"),
              meta: { title: "Swap Pallet" },
            },
            {
              path: "swap-pallet/create",
              name: "swap-pallet-create",
              component: () => import("@/views/inventory-management/swap-pallet/CreateSwapPallet.vue"),
              meta: { title: "Create Swap Pallet" },
            },
            {
              path: "block-stock",
              name: "block-stock",
              component: () => import("@/views/inventory-management/block-pallet/BlockPalletView.vue"),
              meta: { title: "Manage Block Stock" },
            },
            {
              path: "block-stock/create",
              name: "block-stock-create",
              component: () => import("@/views/inventory-management/block-pallet/CreateBlockPallet.vue"),
              meta: { title: "Create Block Stock" },
            },
            {
              path: "block-stock/unblock",
              name: "block-stock-unblock",
              component: () => import("@/views/inventory-management/unblock-pallet/UnblockPalletView.vue"),
              meta: { title: "Create Unblock Stock" },
            },
            // Legacy aliases / redirects
            {
              path: "block-pallet",
              name: "block-pallet",
              redirect: "/inventory-management/block-stock",
            },
            {
              path: "block-pallet/create",
              name: "block-pallet-create",
              redirect: "/inventory-management/block-stock/create",
            },
            {
              path: "unblock-pallet",
              name: "unblock-pallet",
              redirect: "/inventory-management/block-stock/unblock",
            },
            {
              path: "location-check",
              name: "location-check",
              component: () => import("@/views/inventory-management/location-check/LocationCheckView.vue"),
              meta: { title: "Location Check" },
            },
            {
              path: "adjustment-pallet",
              name: "adjustment-pallet",
              component: () => import("@/views/inventory-management/adjustment-pallet/AdjustmentPalletView.vue"),
              meta: { title: "Adjustment Pallet" },
            },
            {
              path: "adjustment-pallet/create",
              name: "adjustment-pallet-create",
              component: () => import("@/views/inventory-management/adjustment-pallet/AdjustmentPallet.vue"),
              meta: { title: "Create Adjustment Pallet" },
            },
            {
              path: "putaway-monitoring",
              name: "putaway-monitoring",
              component: () => import("@/views/inventory-management/putaway-monitoring/PutawayMonitoringView.vue"),
              meta: { title: "Putaway Monitoring" },
            },
            {
              path: "putback-monitoring",
              name: "putback-monitoring",
              component: () => import("@/views/inventory-management/putback-monitoring/PutbackMonitoringView.vue"),
              meta: { title: "Putback Monitoring" },
            },
            {
              path: "picking-monitoring",
              name: "picking-monitoring",
              component: () => import("@/views/inventory-management/picking-monitoring/PickingMonitoringView.vue"),
              meta: { title: "Picking Monitoring" },
            },
            {
              path: "join-pallet",
              name: "join-pallet",
              component: () => import("@/views/inventory-management/join-pallet/JoinPalletView.vue"),
              meta: { title: "Join Pallet" },
            },
            {
              path: "join-pallet/create",
              name: "join-pallet-create",
              component: () => import("@/views/inventory-management/join-pallet/CreateJoinPallet.vue"),
              meta: { title: "Create Join Pallet" },
            },
          ],
        },
        {
          path: "reports",
          children: [
            {
              path: "damage-reports",
              name: "damage-reports",
              component: () => import("@/views/reports/SohDamagedReports.vue"),
              meta: { title: "SOH Damage Report" },
            },
            {
              path: "blocked-reports",
              name: "blocked-reports",
              component: () => import("@/views/reports/SohBlockedReports.vue"),
              meta: { title: "SOH Blocked Report" },
            },
            {
              path: "location-status-reports",
              name: "location-status-reports",
              component: () => import("@/views/reports/LocationStatusReports.vue"),
              meta: { title: "Location Status Report" },
            },
            {
              path: "master-data-item-reports",
              name: "master-data-item-reports",
              component: () => import("@/views/reports/MasterDataItemReports.vue"),
              meta: { title: "Daily Inventory Summary" },
            },
            {
              path: "transaction-history-inbound-reports",
              name: "transaction-history-inbound-reports",
              component: () => import("@/views/reports/TransactionHistoryInboundReports.vue"),
              meta: { title: "Transaction History Inbound Reports" },
            },
            {
              path: "transaction-history-outbound-reports",
              name: "transaction-history-outbound-reports",
              component: () => import("@/views/reports/TransactionHistoryOutboundReports.vue"),
              meta: { title: "Transaction History Outbound Reports" },
            },
            {
              path: "aging-stock-reports",
              name: "aging-stock-reports",
              component: () => import("@/views/reports/AgingStockReports.vue"),
              meta: { title: "Aging Stock Reports" },
            },
            {
              path: "aging-stock-lpn-reports",
              name: "aging-stock-lpn-reports",
              component: () => import("@/views/reports/AgingStockReportsLpn.vue"),
              meta: { title: "Aging Stock LPN Reports" },
            },
            {
              path: "aging-block-lpn-reports",
              name: "aging-block-lpn-reports",
              component: () => import("@/views/reports/AgingStockReportBlockLpn.vue"),
              meta: { title: "Aging Block LPN Reports" },
            },
            {
              path: "aging-stock-pallet-reports",
              name: "aging-stock-pallet-reports",
              component: () => import("@/views/reports/AgingStockReportPallet.vue"),
              meta: { title: "Aging Stock Pallet Reports" },
            },
            {
              path: "pallet-by-customer-reports",
              name: "pallet-by-customer-reports",
              component: () => import("@/views/reports/PalletByCustomerReports.vue"),
              meta: { title: "LPN Report" },
            },
            {
              path: "customer-inventory-balance",
              alias: "pallet-lpn-reports",
              name: "customer-inventory-balance",
              component: () => import("@/views/reports/PalletLpnReports.vue"),
              meta: { title: "Customer Inventory Balance" },
            },
            {
              path: "customer-room-location-reports",
              name: "customer-room-location-reports",
              component: () => import("@/views/reports/CustomerRoomLocationReports.vue"),
              meta: { title: "Customer Room Location" },
            },
            {
              path: "lpn-discrepancy-reports",
              name: "lpn-discrepancy-reports",
              component: () => import("@/views/reports/LpnDiscrepancyReports.vue"),
              meta: { title: "LPN Discrepancy Reports" },
            },
            {
              path: "stock-movement-reports",
              name: "stock-movement-reports",
              component: () => import("@/views/reports/StockMovementReports.vue"),
              meta: { title: "Inventory Activity History" },
            },
            {
              path: "occupancy-reports",
              name: "occupancy-reports",
              component: () => import("@/views/reports/OccupancyReports.vue"),
              meta: { title: "Occupancy Report" },
            },
          ],
        },
        {
          path: "documents",
          name: "documents",
          component: () => import("@/views/dashboard/DocumentsView.vue"),
          meta: { title: "Documents" },
        },
        {
          path: "settings",
          name: "settings",
          component: () => import("@/views/dashboard/SettingsView.vue"),
          meta: { title: "Settings" },
        },
        {
          path: "profile",
          name: "profile",
          component: () => import("@/views/dashboard/ProfileView.vue"),
          meta: { title: "Profile" },
        },
      ],
    },

    // Catch-all 404 route - must be last
    {
      path: "/:pathMatch(.*)*",
      redirect: "/error/404",
    },
  ],
});

// Navigation guards
router.beforeEach((to, from, next) => {
  const token = localStorage.getItem("auth_token");
  const isAuthenticated = !!token;

  // Get user role
  let userRole = null;
  let user = null;

  if (isAuthenticated) {
    try {
      const savedUser = localStorage.getItem("auth_user");
      if (savedUser) {
        user = JSON.parse(savedUser);
        const role = user?.role || user?.role_name || null;

        // Handle role as object or string
        if (typeof role === 'object' && role !== null) {
          userRole = role.name || role.display_name || null;
        } else {
          userRole = role;
        }
      }
    } catch (e) {
      console.error("Failed to parse user data:", e);
    }
  }

  // Check if route exists (prevent navigation to non-existent routes)
  if (to.matched.length === 0 && to.name !== "error") {
    // Route not found, redirect to 404 error page
    next({
      name: "error",
      params: { code: "404" },
      replace: true,
    });
    return;
  }

  // Check if route requires authentication
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      next({
        name: "login",
        query: { redirect: to.fullPath },
      });
      return;
    }

    // RBAC: Check if user has access to this route
    const routeName = to.name;
    
    if (routeName && userRole) {
      // Use the centralized RBAC function
      const canAccess = hasRouteAccess(userRole, routeName);

      if (!canAccess) {
        console.log(`[RBAC] Blocking ${userRole} from accessing: ${routeName}`);

        showAccessDeniedToast();

        // Stay on the current page instead of forcing a redirect.
        // Fall back to the role's default route only if there's no current page to stay on
        // (e.g. a direct/deep link on first load).
        if (from.matched.length > 0) {
          next(false);
        } else {
          const defaultRoute = getDefaultRoute(userRole);
          if (defaultRoute !== routeName) {
            next({ name: defaultRoute, replace: true });
          } else {
            next(false);
          }
        }
        return;
      }
    }

    next();
    return;
  }
  // Check if route requires guest (already logged in users shouldn't access)
  else if (to.matched.some((record) => record.meta.requiresGuest)) {
    if (isAuthenticated) {
      // Redirect based on user role using centralized config
      const defaultRoute = getDefaultRoute(userRole);
      next({ name: defaultRoute, replace: true });
      return;
    } else {
      next();
      return;
    }
  }
  // Public routes
  else {
    next();
    return;
  }
});

// Global error handler for navigation errors
router.onError((error) => {
  console.error("Router error:", error);

  // Handle chunk loading errors (common in production)
  if (error.message.includes("Failed to fetch dynamically imported module") || error.message.includes("Importing a module script failed")) {
    // Reload the page to fetch the latest chunks
    window.location.reload();
  } else {
    // Navigate to error page for other errors
    router.push({
      name: "error",
      params: { code: "default" },
      query: { message: "A navigation error occurred. Please try again." },
    });
  }
});

// Set page title
router.afterEach((to) => {
  const baseTitle = "WMS Admin";
  document.title = to.meta.title ? `${to.meta.title} - ${baseTitle}` : baseTitle;
});

export default router;
